import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

/** Steuerkategorie nach UNTDID 5305 (E-Rechnung BT-118). */
export type SteuerKategorie = 'S' | 'Z' | 'E' | 'AE' | 'K' | 'G' | 'O';

export const KATEGORIE_LABEL: Record<SteuerKategorie, string> = {
  S: 'S – Standardsatz',
  Z: 'Z – Nullsatz',
  E: 'E – steuerbefreit',
  AE: 'AE – Reverse-Charge (§13b UStG)',
  K: 'K – innergemeinschaftliche Lieferung',
  G: 'G – Ausfuhr',
  O: 'O – nicht steuerbar',
};

export interface Steuersatz {
  steuersatz_id: number;
  bezeichnung: string;
  prozent: number;
  kategorie: SteuerKategorie;
  befreiungsgrund: string | null;
  befreiungsgrund_code: string | null;
  ist_standard: boolean;
  aktiv: boolean;
}

const LEERES_FORMULAR = {
  bezeichnung: '',
  prozent: '',
  kategorie: 'E' as SteuerKategorie,
  befreiungsgrund: '',
  befreiungsgrund_code: '',
};

/**
 * Pflege der Steuersatz-Stammdaten. Die Sätze werden in der Artikelmaske als
 * Dropdown angeboten; der Prozentwert eines Satzes propagiert bei Änderung
 * auf alle verknüpften Artikel (Belege behalten ihren eingefrorenen Satz).
 *
 * Seit E-Rechnung Paket 1 trägt jeder Satz eine Steuerkategorie: > 0 % ist
 * immer S, ein 0-%-Satz braucht eine Befreiungskategorie (E/AE/K/G/O/Z) und
 * einen Befreiungsgrund – er landet als Klartext auf Rechnung und XML.
 * Die Sätze für §13b und ig. Lieferung werden inaktiv vorangelegt und hier
 * bewusst freigeschaltet.
 */
const SteuersatzSection: React.FC = () => {
  const [saetze, setSaetze] = useState<Steuersatz[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(LEERES_FORMULAR);

  const load = useCallback(() => {
    setLoading(true);
    setFehler(null);
    axios.get<Steuersatz[]>(`${API_URL}/api/steuersaetze`)
      .then(r => setSaetze(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        setFehler(`Steuersätze konnten nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const anfrage = async (fn: () => Promise<unknown>) => {
    setFehler(null);
    setSaving(true);
    try {
      await fn();
      load();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      setFehler(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const prozentWert = parseFloat(form.prozent.replace(',', '.'));
  const istNullsatz = Number.isFinite(prozentWert) && prozentWert === 0;

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!Number.isFinite(prozentWert)) {
      setFehler('Bitte einen gültigen Prozentwert angeben.');
      return;
    }
    if (istNullsatz && form.kategorie === 'S') {
      setFehler('Ein Satz von 0 % braucht eine Befreiungskategorie (nicht S).');
      return;
    }
    if (istNullsatz && !form.befreiungsgrund.trim()) {
      setFehler('Bitte den Befreiungsgrund angeben – er erscheint als Klartext auf der Rechnung.');
      return;
    }
    anfrage(async () => {
      await axios.post(`${API_URL}/api/steuersaetze`, {
        bezeichnung: form.bezeichnung,
        prozent: prozentWert,
        kategorie: istNullsatz ? form.kategorie : 'S',
        befreiungsgrund: istNullsatz ? form.befreiungsgrund : null,
        befreiungsgrund_code: istNullsatz ? (form.befreiungsgrund_code || null) : null,
      });
      setForm(LEERES_FORMULAR);
    });
  };

  const handleProzentKorrigieren = (s: Steuersatz) => {
    const eingabe = window.prompt(
      `Prozentwert für "${s.bezeichnung}" ändern — verknüpfte Artikel übernehmen den neuen Satz, bestehende Belege behalten ihren eingefrorenen Wert:`,
      String(s.prozent),
    );
    if (eingabe === null) return;
    const wert = parseFloat(eingabe.replace(',', '.'));
    if (!Number.isFinite(wert) || wert < 0 || wert > 100) {
      setFehler('Ungültiger Prozentwert — erlaubt sind 0 bis 100.');
      return;
    }
    const payload: Record<string, unknown> = { prozent: wert };
    if (wert === 0 && s.kategorie === 'S') {
      // 0 % ist nie S: Kategorie + Grund im selben Schritt erfragen.
      const grund = window.prompt(
        'Ein Satz von 0 % braucht einen Befreiungsgrund (Kategorie E – steuerbefreit; ' +
        'andere Kategorien danach über "Kategorie ändern"):',
        'Steuerfreier Umsatz nach §4 UStG',
      );
      if (grund === null) return;
      payload.kategorie = 'E';
      payload.befreiungsgrund = grund;
    } else if (wert > 0 && s.kategorie !== 'S') {
      payload.kategorie = 'S';
    }
    anfrage(() => axios.put(`${API_URL}/api/steuersaetze/${s.steuersatz_id}`, payload));
  };

  const handleGrundKorrigieren = (s: Steuersatz) => {
    const eingabe = window.prompt(
      `Befreiungsgrund für "${s.bezeichnung}" (${KATEGORIE_LABEL[s.kategorie]}) — erscheint als Klartext auf Rechnung und E-Rechnung:`,
      s.befreiungsgrund ?? '',
    );
    if (eingabe === null) return;
    anfrage(() => axios.put(`${API_URL}/api/steuersaetze/${s.steuersatz_id}`, { befreiungsgrund: eingabe }));
  };

  const handleKategorieAendern = (s: Steuersatz) => {
    const codes = (Object.keys(KATEGORIE_LABEL) as SteuerKategorie[]).filter(k => k !== 'S');
    const eingabe = window.prompt(
      `Steuerkategorie für "${s.bezeichnung}" (0 %):\n` +
      codes.map(k => `  ${KATEGORIE_LABEL[k]}`).join('\n') +
      `\n\nKürzel eingeben:`,
      s.kategorie,
    );
    if (eingabe === null) return;
    const neu = eingabe.trim().toUpperCase() as SteuerKategorie;
    if (!codes.includes(neu)) {
      setFehler(`Unbekannte Kategorie "${eingabe}". Erlaubt: ${codes.join(', ')}.`);
      return;
    }
    let grund = s.befreiungsgrund ?? '';
    if (!grund.trim() || neu !== s.kategorie) {
      const g = window.prompt('Befreiungsgrund (Klartext auf der Rechnung):', grund);
      if (g === null) return;
      grund = g;
    }
    anfrage(() => axios.put(`${API_URL}/api/steuersaetze/${s.steuersatz_id}`, {
      kategorie: neu, befreiungsgrund: grund,
    }));
  };

  const handleStandard = (s: Steuersatz) =>
    anfrage(() => axios.put(`${API_URL}/api/steuersaetze/${s.steuersatz_id}`, { ist_standard: true }));

  const handleAktivToggle = (s: Steuersatz) =>
    anfrage(() => axios.put(`${API_URL}/api/steuersaetze/${s.steuersatz_id}`, { aktiv: !s.aktiv }));

  const handleLoeschen = (s: Steuersatz) => {
    if (!window.confirm(`Steuersatz "${s.bezeichnung}" (${s.prozent} %) löschen?`)) return;
    anfrage(() => axios.delete(`${API_URL}/api/steuersaetze/${s.steuersatz_id}`));
  };

  const feldStil: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 };

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Steuersätze</h3>
      <p style={{ color: 'var(--ink-2)', marginBottom: 16, fontSize: 14 }}>
        Stammdaten für die Umsatzsteuer. Artikel werden mit einem Satz verknüpft;
        Belege frieren Satz und Steuerkategorie zum Belegzeitpunkt ein. Für
        Reverse-Charge (§13b UStG) oder innergemeinschaftliche Lieferungen den
        passenden 0-%-Satz hier aktivieren und am Auftrag bzw. Artikel wählen.
      </p>

      {fehler && (
        <div style={{ color: 'var(--red)', border: '1px solid var(--red)', padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
          {fehler}
        </div>
      )}

      <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, marginBottom: 18, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={feldStil}>
          Bezeichnung
          <input
            value={form.bezeichnung}
            onChange={e => setForm(f => ({ ...f, bezeichnung: e.target.value }))}
            placeholder="z.B. Ermäßigter Satz"
            required
            style={{ padding: '7px 10px', minWidth: 200 }}
          />
        </label>
        <label style={feldStil}>
          Prozent
          <input
            value={form.prozent}
            onChange={e => setForm(f => ({ ...f, prozent: e.target.value }))}
            placeholder="7"
            required
            inputMode="decimal"
            style={{ padding: '7px 10px', width: 90 }}
          />
        </label>
        {istNullsatz && (
          <>
            <label style={feldStil}>
              Kategorie
              <select
                value={form.kategorie}
                onChange={e => setForm(f => ({ ...f, kategorie: e.target.value as SteuerKategorie }))}
                style={{ padding: '7px 10px' }}
              >
                {(Object.keys(KATEGORIE_LABEL) as SteuerKategorie[]).filter(k => k !== 'S').map(k => (
                  <option key={k} value={k}>{KATEGORIE_LABEL[k]}</option>
                ))}
              </select>
            </label>
            <label style={{ ...feldStil, flex: 1, minWidth: 260 }}>
              Befreiungsgrund (Klartext auf der Rechnung)
              <input
                value={form.befreiungsgrund}
                onChange={e => setForm(f => ({ ...f, befreiungsgrund: e.target.value }))}
                placeholder="z.B. Steuerschuldnerschaft des Leistungsempfängers (§13b UStG)"
                required
                style={{ padding: '7px 10px' }}
              />
            </label>
          </>
        )}
        <button type="submit" className="k-btn k-btn--primary" disabled={saving}>
          + Anlegen
        </button>
      </form>

      {loading ? (
        <div>Lade Steuersätze…</div>
      ) : (
        <div className="table-container">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 10px 6px 0' }}>Bezeichnung</th>
                <th style={{ textAlign: 'right', padding: '6px 10px' }}>Prozent</th>
                <th style={{ textAlign: 'left', padding: '6px 10px' }}>Kategorie</th>
                <th style={{ textAlign: 'center', padding: '6px 10px' }}>Standard</th>
                <th style={{ textAlign: 'center', padding: '6px 10px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '6px 0' }}></th>
              </tr>
            </thead>
            <tbody>
              {saetze.map(s => (
                <tr key={s.steuersatz_id} style={{ borderBottom: '1px solid var(--bone-3)', opacity: s.aktiv ? 1 : 0.55 }}>
                  <td style={{ padding: '8px 10px 8px 0' }}>{s.bezeichnung}</td>
                  <td className="is-num" style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {s.prozent.toLocaleString('de-DE')} %
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>
                    <div>{KATEGORIE_LABEL[s.kategorie] ?? s.kategorie}</div>
                    {s.kategorie !== 'S' && (
                      <div style={{ color: 'var(--ink-2)', fontSize: 12 }} title={s.befreiungsgrund_code ?? undefined}>
                        {s.befreiungsgrund || <em>Befreiungsgrund fehlt</em>}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    {s.ist_standard ? (
                      <strong>Standard</strong>
                    ) : (
                      <button type="button" className="k-btn k-btn--sm k-btn--ghost" disabled={saving} onClick={() => handleStandard(s)}>
                        Als Standard
                      </button>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <button type="button" className="k-btn k-btn--sm k-btn--ghost" disabled={saving} onClick={() => handleAktivToggle(s)}>
                      {s.aktiv ? 'Aktiv' : 'Inaktiv'}
                    </button>
                  </td>
                  <td style={{ padding: '8px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" className="k-btn k-btn--sm k-btn--ghost" disabled={saving} onClick={() => handleProzentKorrigieren(s)} style={{ marginRight: 6 }}>
                      Prozent ändern
                    </button>
                    {s.kategorie !== 'S' && (
                      <>
                        <button type="button" className="k-btn k-btn--sm k-btn--ghost" disabled={saving} onClick={() => handleKategorieAendern(s)} style={{ marginRight: 6 }}>
                          Kategorie ändern
                        </button>
                        <button type="button" className="k-btn k-btn--sm k-btn--ghost" disabled={saving} onClick={() => handleGrundKorrigieren(s)} style={{ marginRight: 6 }}>
                          Grund ändern
                        </button>
                      </>
                    )}
                    <button type="button" className="k-btn k-btn--sm k-btn--danger" disabled={saving} onClick={() => handleLoeschen(s)}>
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SteuersatzSection;
